import {
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { FormatCurrency, Text } from '@vestwell-frontend/ui';
import { useWentworthBalanceGetActivitySummary } from '@vestwell-unified-api/hooks';

import { subMonths } from 'date-fns';
import { FC, Fragment, useMemo, useState } from 'react';

import {
  LastTableRowNoBorderTable,
  PrimaryTableCell,
  SecondaryTableCell
} from '../BalancesTable.component';
import { CUSTOM_ACTIVITIES_ORDER, FUNDING_SOURCE_LABELS } from '../consts';
import { CustomRangeFilter } from './CustomRangeFilter';
import { toISODate, toLocalDate } from './utils';

export type ActivitySummaryTileProps = {
  employeeId: number;
};

const today = toISODate(new Date());

export const ActivitySummary: FC<ActivitySummaryTileProps> = props => {
  const [formValues, setFormValues] = useState<{
    endDate?: string;
    startDate?: string;
  }>({
    endDate: today,
    startDate: toISODate(subMonths(new Date(), 12))
  });

  const activities = useWentworthBalanceGetActivitySummary(
    {
      endDate: formValues.endDate,
      participantId: props.employeeId,
      startDate: formValues.startDate
    },
    {
      query: {
        enabled: !!props.employeeId,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        useErrorBoundary: true
      }
    }
  );

  const rows = useMemo(() => {
    const mappedRows = activities.data?.summary.map((item, index) => ({
      amount: item.totalValue,
      detailRows: null,
      id: index + 1,
      label:
        (item.groupByCategory as unknown as string) === 'Income'
          ? 'Dividends'
          : (item.groupByCategory as unknown as string) === 'Gain/Losses'
            ? 'Gain / Loss'
            : [
                  'Loan Repayments',
                  'Loan Disbursements',
                  'Conversions',
                  'Corrective Withdrawals',
                  'Fees'
                ].includes(item.groupByCategory as unknown as string)
              ? item.groupByCategory
              : ['Withdrawals', 'Forfeitures/Suspense'].includes(
                    item.groupByCategory as unknown as string
                  )
                ? 'Withdrawals'
                : 'Miscellaneous'
    }));

    if (activities.data?.rolloverDetails) {
      mappedRows.unshift({
        amount: activities.data?.rolloverDetails.total,
        detailRows: [
          {
            'Pre-tax Rollover': activities.data?.rolloverDetails.preTax
          },
          {
            'Roth Rollover': activities.data?.rolloverDetails.roth
          }
        ].filter(row => Object.values(row)[0] > 0),
        id: mappedRows.length + 1,
        label: 'Rollovers In'
      });
    }

    if (activities.data?.employerDetails.length > 0) {
      mappedRows.unshift({
        amount: activities.data?.employerDetails.reduce(
          (total, employer) => total + employer.totalBalance,
          0
        ),
        detailRows: activities.data?.employerDetails
          .map(employer => ({
            [FUNDING_SOURCE_LABELS[employer.fundingSource] ||
            employer.fundingSource]: employer.totalBalance
          }))
          .filter(row => Object.values(row)[0] > 0),
        id: mappedRows.length + 1,
        label: 'Employer Contributions'
      });
    }

    if (activities.data?.employeeDetails) {
      mappedRows.unshift({
        amount: activities.data?.employeeDetails.total,
        detailRows: [
          {
            'After-tax': activities.data?.employeeDetails.afterTax
          },
          {
            'Pre-tax': activities.data?.employeeDetails.preTax
          },
          {
            Roth: activities.data?.employeeDetails.roth
          }
        ].filter(row => Object.values(row)[0] > 0),
        id: mappedRows.length + 1,
        label: 'My Contributions'
      });
    }

    const filteredRows = mappedRows?.filter(row => row.amount != 0);

    return filteredRows?.sort((a, b) => {
      return (
        CUSTOM_ACTIVITIES_ORDER.indexOf(a.label) -
        CUSTOM_ACTIVITIES_ORDER.indexOf(b.label)
      );
    });
  }, [activities.data]);

  return (
    <>
      <CustomRangeFilter
        formValues={formValues}
        setFormValues={setFormValues}
      />
      <TableContainer>
        <LastTableRowNoBorderTable data-testid='activity-summary-table'>
          <TableHead>
            <TableRow>
              <TableCell>Transaction Summary</TableCell>
              <TableCell align='right'>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell role='rowheader'>
                <Stack>
                  <Text
                    fontSize='body1.fontSize'
                    fontWeight='bold'
                    isLoading={activities.isLoading || activities.isFetching}
                    mb={0}>
                    Beginning Balance
                    {!formValues.startDate
                      ? ''
                      : ` - ${toLocalDate(formValues.startDate)}`}
                  </Text>
                </Stack>
              </TableCell>
              <TableCell align='right'>
                <Text
                  fontSize='body1.fontSize'
                  fontWeight='bold'
                  isLoading={activities.isLoading || activities.isFetching}
                  mb={0}>
                  <FormatCurrency
                    negativeNotation='minus'
                    precision={2}
                    stripTrailingZeros={false}
                    value={activities.data?.startingBalance}
                  />
                </Text>
              </TableCell>
            </TableRow>
            {rows?.map(row => (
              <Fragment key={row.id}>
                <TableRow>
                  <PrimaryTableCell role='rowheader'>
                    <Stack alignItems='center' direction='row' spacing={2}>
                      <Stack>
                        <Text mb={0}>{row.label}</Text>
                      </Stack>
                    </Stack>
                  </PrimaryTableCell>
                  <PrimaryTableCell align='right'>
                    <Text
                      color={
                        row.label === 'Gain / Loss' && row.amount >= 0
                          ? 'success'
                          : undefined
                      }
                      isLoading={activities.isLoading || activities.isFetching}
                      mb={0}>
                      <FormatCurrency
                        negativeNotation='minus'
                        precision={2}
                        stripTrailingZeros={false}
                        value={row.amount}
                      />
                    </Text>
                  </PrimaryTableCell>
                </TableRow>
                {row.detailRows?.map(row =>
                  Object.entries(row).map(([key, value]) => (
                    <TableRow key={key}>
                      <SecondaryTableCell>{key}</SecondaryTableCell>
                      <SecondaryTableCell align='right'>
                        <Text
                          isLoading={
                            activities.isLoading || activities.isFetching
                          }
                          mb={0}>
                          <FormatCurrency
                            negativeNotation='minus'
                            precision={2}
                            stripTrailingZeros={false}
                            value={Number(value)}
                          />
                        </Text>
                      </SecondaryTableCell>
                    </TableRow>
                  ))
                )}
              </Fragment>
            ))}
            <TableRow>
              <TableCell>
                <Stack>
                  <Text
                    fontSize='body1.fontSize'
                    fontWeight='bold'
                    isLoading={activities.isLoading || activities.isFetching}
                    mb={0}>
                    Ending Balance - {toLocalDate(formValues.endDate)}
                  </Text>
                </Stack>
              </TableCell>
              <TableCell align='right'>
                <Text
                  fontSize='body1.fontSize'
                  fontWeight='bold'
                  isLoading={activities.isLoading || activities.isFetching}
                  mb={0}>
                  <FormatCurrency
                    negativeNotation='minus'
                    precision={2}
                    stripTrailingZeros={false}
                    value={activities.data?.endingBalance}
                  />
                </Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </LastTableRowNoBorderTable>
      </TableContainer>
    </>
  );
};
